import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Logo from "@/components/feature/Logo";
import ThemeToggle from "@/components/feature/ThemeToggle";
import { useAuth } from "@/context/AuthContext";

interface NavItem {
  label: string;
  to: string;
  icon: string;
}

const adminNav: NavItem[] = [
  { label: "Panel", to: "/admin/panel", icon: "ri-dashboard-line" },
  { label: "Solicitudes", to: "/admin/solicitudes", icon: "ri-file-list-3-line" },
  { label: "Afiliados", to: "/admin/afiliados", icon: "ri-user-shared-line" },
  { label: "Flota", to: "/admin/flota", icon: "ri-truck-line" },
  { label: "Servicios", to: "/admin/servicios", icon: "ri-service-line" },
  { label: "Héroe y medios", to: "/admin/hero", icon: "ri-image-line" },
  { label: "Galería", to: "/admin/galeria", icon: "ri-gallery-line" },
  { label: "Configuración", to: "/admin/configuracion", icon: "ri-settings-3-line" },
  { label: "Usuarios", to: "/admin/usuarios", icon: "ri-team-line" },
];

function navItemClass(isActive: boolean) {
  return `flex items-center gap-3 rounded-md px-3.5 py-2.5 text-sm font-label font-medium transition-colors duration-200 ${
    isActive
      ? "bg-primary-500 text-background-50 dark:text-foreground-950"
      : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
  }`;
}

export default function AdminLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const displayName =
    (user?.user_metadata?.nombre as string | undefined) || user?.email?.split("@")[0] || "Administrador";

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login", { replace: true });
  };

  const sidebarContent = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b border-background-200 px-5">
        <Logo />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Navegación del panel">
        <p className="px-3.5 pb-2 text-[11px] font-label font-semibold uppercase tracking-[0.14em] text-foreground-400">
          Gestión
        </p>
        <div className="flex flex-col gap-1">
          {adminNav.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => navItemClass(isActive)}>
              <i className={`${item.icon} text-lg leading-none`} aria-hidden="true"></i>
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="border-t border-background-200 p-3">
        <div className="flex items-center gap-3 rounded-md bg-background-100 px-3.5 py-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-sm font-label font-semibold text-background-50 dark:text-foreground-950">
            {displayName.charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-label font-semibold text-foreground-900">{displayName}</p>
            <p className="truncate text-xs text-foreground-500">{user?.email}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSignOut}
          className="mt-2 flex w-full cursor-pointer items-center gap-3 rounded-md px-3.5 py-2.5 text-sm font-label font-medium text-foreground-600 transition-colors duration-200 hover:bg-primary-50 hover:text-primary-700"
        >
          <i className="ri-logout-box-r-line text-lg leading-none" aria-hidden="true"></i>
          Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background-100">
      <Seo
        title="Panel de administración"
        path={location.pathname}
        robots="noindex, nofollow, noarchive"
      />
      {/* Barra lateral fija (escritorio) */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[264px] border-r border-background-200 bg-background-50 lg:block">
        {sidebarContent}
      </aside>

      {/* Barra superior */}
      <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-background-200 bg-background-50/95 px-4 backdrop-blur-md lg:pl-[280px] lg:pr-6">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Abrir menú del panel"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
        >
          <i className="ri-menu-line text-xl leading-none" aria-hidden="true"></i>
        </button>
        <div className="flex-1">
          <span className="text-sm font-label font-semibold text-foreground-500">
            Panel de administración
          </span>
        </div>
        <ThemeToggle />
      </header>

      {/* Menú móvil */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Cerrar menú del panel"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-foreground-950/40"
          ></button>
          <div className="absolute inset-y-0 left-0 w-[280px] max-w-[85%] border-r border-background-200 bg-background-50">
            {sidebarContent}
          </div>
        </div>
      )}

      <main className="lg:pl-[264px]">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}